import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

const PanelControlAcademico = () => {
  const API = `${window.location.origin}/api`;
  const [clases, setClases] = useState([]);
  const [filteredClases, setFilteredClases] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [estudiantes, setEstudiantes] = useState([]);
  const [calificaciones, setCalificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('activas'); // Por defecto mostrar solo activas
  const [semestreSeleccionado, setSemestreSeleccionado] = useState('todos');
  const [estudianteSeleccionado, setEstudianteSeleccionado] = useState('todos'); // Para filtro de gráfica
  const [metricas, setMetricas] = useState({
    totalEstudiantes: 0,
    totalProfesores: 0,
    clasesActivas: 0,
    promedioGeneral: 0,
    clasesInactivas: 0,
    estudiantesActivos: 0
  });
  const [profesoresPendientes, setProfesoresPendientes] = useState([]);

  useEffect(() => {
    cargarDatos();
    cargarProfesoresPendientes();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [filtroEstado, semestreSeleccionado, clases]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Cargar todos los datos en paralelo
      const [responseClases, responseSemestres, responseUsuarios, responseCalificaciones] = await Promise.all([
        axios.get(`${API}/clases`, { headers }),
        axios.get(`${API}/semestres`, { headers }),
        axios.get(`${API}/users`, { headers }),  // Cambiado de /admin/usuarios a /users
        axios.get(`${API}/calificaciones`, { headers }).catch((err) => {
          console.log('Error cargando calificaciones:', err);
          return { data: [] };
        })
      ]);
      
      console.log('Datos cargados:', {
        clases: responseClases.data.length,
        semestres: responseSemestres.data.length,
        usuarios: responseUsuarios.data.length,
        calificaciones: responseCalificaciones.data.length
      });
      
      // Ordenar clases por fecha de inicio (más antiguas primero - orden cronológico)
      const clasesOrdenadas = responseClases.data.sort((a, b) => {
        if (!a.fecha_inicio && !b.fecha_inicio) return 0;
        if (!a.fecha_inicio) return 1;
        if (!b.fecha_inicio) return -1;
        
        const fechaA = new Date(a.fecha_inicio);
        const fechaB = new Date(b.fecha_inicio);
        
        if (isNaN(fechaA.getTime()) && isNaN(fechaB.getTime())) return 0;
        if (isNaN(fechaA.getTime())) return 1;
        if (isNaN(fechaB.getTime())) return -1;
        
        return fechaA.getTime() - fechaB.getTime();
      });
      
      // Separar usuarios por rol
      const usuariosData = responseUsuarios.data;
      // Los profesores incluyen tanto rol 'profesor' como 'admin' (ya que admins también dan clases)
      const profesoresData = usuariosData.filter(u => u.rol === 'profesor' || u.rol === 'admin');
      const estudiantesData = usuariosData.filter(u => u.rol === 'estudiante');
      
      console.log('Usuarios filtrados:', {
        profesores: profesoresData.length,
        estudiantes: estudiantesData.length
      });
      
      setClases(clasesOrdenadas);
      setSemestres(responseSemestres.data);
      setProfesores(profesoresData);
      setEstudiantes(estudiantesData);
      setCalificaciones(responseCalificaciones.data);
      
      // Calcular métricas generales
      calcularMetricas(clasesOrdenadas, profesoresData, estudiantesData, responseCalificaciones.data);
      
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarProfesoresPendientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/users`, { headers });
      // Mostrar profesores con capacitado explícitamente false
      const profesores = response.data.filter(u => 
        u.rol === 'profesor' && 
        u.capacitado === false
      );
      
      setProfesoresPendientes(profesores);
    } catch (error) {
      console.error('Error cargando profesores pendientes:', error);
    }
  };

  const activarProfesor = async (profesorId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/admin/profesor/${profesorId}/activar`, {}, { headers });
      
      // Recargar la lista
      await cargarProfesoresPendientes();
      await cargarDatos();
      
      alert('Profesor activado exitosamente');
    } catch (error) {
      console.error('Error activando profesor:', error);
      alert('Error al activar profesor');
    }
  };

  const calcularMetricas = (clasesData, profesoresData, estudiantesData, calificacionesData) => {
    const clasesActivas = clasesData.filter(c => c.activa === true).length;
    const clasesInactivas = clasesData.filter(c => c.activa === false).length;
    
    // Calcular promedio general del instituto
    let promedioGeneral = 0;
    if (calificacionesData.length > 0) {
      // Agrupar por estudiante y clase para calcular promedio de promedios
      const promediosPorEstudiante = {};
      calificacionesData.forEach(cal => {
        const key = `${cal.estudiante_id}_${cal.clase_id}`;
        if (!promediosPorEstudiante[key]) {
          promediosPorEstudiante[key] = [];
        }
        promediosPorEstudiante[key].push(cal.calificacion);
      });
      
      const promediosClases = Object.values(promediosPorEstudiante).map(notas => {
        return notas.reduce((sum, nota) => sum + nota, 0) / notas.length;
      });
      
      if (promediosClases.length > 0) {
        promedioGeneral = promediosClases.reduce((sum, prom) => sum + prom, 0) / promediosClases.length;
      }
    }
    
    // Estudiantes activos (con al menos una calificación)
    const estudiantesConCalificaciones = new Set(calificacionesData.map(c => c.estudiante_id));
    
    setMetricas({
      totalEstudiantes: estudiantesData.length,
      totalProfesores: profesoresData.length,
      clasesActivas,
      clasesInactivas,
      promedioGeneral: promedioGeneral.toFixed(1),
      estudiantesActivos: estudiantesConCalificaciones.size
    });
  };

  const aplicarFiltros = () => {
    let resultado = [...clases];

    // Filtrar por estado (activa/inactiva)
    if (filtroEstado === 'activas') {
      resultado = resultado.filter(clase => clase.activa === true);
    } else if (filtroEstado === 'inactivas') {
      resultado = resultado.filter(clase => clase.activa === false);
    }

    // Filtrar por semestre
    if (semestreSeleccionado !== 'todos') {
      // Encontrar el semester_number del semestre seleccionado
      const semestreObj = semestres.find(s => s.id === semestreSeleccionado);
      if (semestreObj) {
        const semestreNumber = semestreObj.semester_number || semestreObj.numero;
        resultado = resultado.filter(clase => 
          clase.semestres && clase.semestres.includes(semestreNumber)
        );
      }
    }

    // Asegurar ordenamiento por fecha de inicio (más antiguas primero - orden cronológico)
    resultado.sort((a, b) => {
      // Si alguna no tiene fecha, va al final
      if (!a.fecha_inicio && !b.fecha_inicio) return 0;
      if (!a.fecha_inicio) return 1;
      if (!b.fecha_inicio) return -1;
      
      const fechaA = new Date(a.fecha_inicio);
      const fechaB = new Date(b.fecha_inicio);
      
      // Verificar si las fechas son válidas
      if (isNaN(fechaA.getTime()) && isNaN(fechaB.getTime())) return 0;
      if (isNaN(fechaA.getTime())) return 1;
      if (isNaN(fechaB.getTime())) return -1;
      
      // Ordenar de más antigua a más reciente (fechaA - fechaB)
      return fechaA.getTime() - fechaB.getTime();
    });

    setFilteredClases(resultado);
  };

  const calcularProgreso = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return { porcentaje: 0, diasTranscurridos: 0, diasRestantes: 0, diasTotales: 0 };

    const ahora = new Date();
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);

    const tiempoTotal = fin - inicio;
    const tiempoTranscurrido = ahora - inicio;
    const tiempoRestante = fin - ahora;

    const diasTotales = Math.ceil(tiempoTotal / (1000 * 60 * 60 * 24));
    const diasTranscurridos = Math.max(0, Math.ceil(tiempoTranscurrido / (1000 * 60 * 60 * 24)));
    const diasRestantes = Math.max(0, Math.ceil(tiempoRestante / (1000 * 60 * 60 * 24)));

    let porcentaje = tiempoTotal > 0 ? (tiempoTranscurrido / tiempoTotal) * 100 : 0;
    porcentaje = Math.max(0, Math.min(100, porcentaje));

    return {
      porcentaje: Math.round(porcentaje),
      diasTranscurridos,
      diasRestantes,
      diasTotales
    };
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'No definida';
    return new Date(fecha).toLocaleDateString('es-UY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getSemestreNombre = (semestreId) => {
    const semestre = semestres.find(s => s.id === semestreId);
    if (!semestre) return `Semestre ${semestreId}`;
    
    // Usar el campo 'name' que es el que se usa en Gestión de Semestres
    return semestre.name || semestre.nombre || `Semestre ${semestre.semester_number || semestre.numero || ''}`;
  };

  const getDiasFormateados = (dias) => {
    if (!dias || dias.length === 0) return 'No definido';
    const diasMap = {
      'lunes': 'Lun',
      'martes': 'Mar',
      'miércoles': 'Mié',
      'miercoles': 'Mié',
      'jueves': 'Jue',
      'viernes': 'Vie',
      'sábado': 'Sáb',
      'sabado': 'Sáb',
      'domingo': 'Dom'
    };
    return dias.map(d => diasMap[d.toLowerCase()] || d).join(', ');
  };

  // Calcular actividad de profesores
  const calcularActividadProfesores = () => {
    return profesores.map(profesor => {
      // Clases asignadas
      const clasesProfesor = clases.filter(c => c.profesor_id === profesor.id);
      
      // Total de estudiantes que atiende
      const totalEstudiantes = clasesProfesor.reduce((sum, clase) => {
        return sum + (clase.estudiantes_inscritos?.length || 0);
      }, 0);
      
      // Última conexión (login)
      const ultimaConexion = profesor.ultima_conexion ? new Date(profesor.ultima_conexion) : null;
      
      // Última actividad (heartbeat)
      const ultimaActividad = profesor.ultima_actividad ? new Date(profesor.ultima_actividad) : null;
      
      // Usar la más reciente entre conexión y actividad
      const ultimaActividadReal = ultimaActividad && ultimaConexion 
        ? (ultimaActividad > ultimaConexion ? ultimaActividad : ultimaConexion)
        : (ultimaActividad || ultimaConexion);
      
      // Calcular días desde última actividad
      let diasInactivo = null;
      let horasInactivo = null;
      if (ultimaActividadReal) {
        const hoy = new Date();
        const diffMs = hoy - ultimaActividadReal;
        diasInactivo = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        horasInactivo = Math.floor(diffMs / (1000 * 60 * 60));
      }
      
      return {
        ...profesor,
        clasesAsignadas: clasesProfesor.length,
        clasesActivas: clasesProfesor.filter(c => c.activa).length,
        totalEstudiantes,
        ultimaConexion,
        ultimaActividad,
        ultimaActividadReal,
        diasInactivo,
        horasInactivo
      };
    }).sort((a, b) => {
      // Ordenar por última actividad (más reciente primero)
      if (!a.ultimaActividadReal && !b.ultimaActividadReal) return 0;
      if (!a.ultimaActividadReal) return 1;
      if (!b.ultimaActividadReal) return -1;
      return b.ultimaActividadReal - a.ultimaActividadReal;
    });
  };

  // Calcular métricas de rendimiento por clase
  const calcularRendimientoClases = () => {
    return filteredClases.map(clase => {
      const calificacionesClase = calificaciones.filter(cal => cal.clase_id === clase.id);
      
      if (calificacionesClase.length === 0) {
        return {
          ...clase,
          promedioClase: 0,
          totalCalificaciones: 0,
          tasaAprobacion: 0,
          estudiantesCalificados: 0
        };
      }
      
      // Agrupar por estudiante
      const calPorEstudiante = {};
      calificacionesClase.forEach(cal => {
        if (!calPorEstudiante[cal.estudiante_id]) {
          calPorEstudiante[cal.estudiante_id] = [];
        }
        calPorEstudiante[cal.estudiante_id].push(cal.calificacion);
      });
      
      // Calcular promedio por estudiante
      const promediosEstudiantes = Object.values(calPorEstudiante).map(notas => {
        return notas.reduce((sum, nota) => sum + nota, 0) / notas.length;
      });
      
      // Promedio general de la clase
      const promedioClase = promediosEstudiantes.reduce((sum, prom) => sum + prom, 0) / promediosEstudiantes.length;
      
      // Tasa de aprobación (>= 70)
      const aprobados = promediosEstudiantes.filter(prom => prom >= 70).length;
      const tasaAprobacion = (aprobados / promediosEstudiantes.length) * 100;
      
      return {
        ...clase,
        promedioClase: promedioClase.toFixed(1),
        totalCalificaciones: calificacionesClase.length,
        tasaAprobacion: tasaAprobacion.toFixed(0),
        estudiantesCalificados: promediosEstudiantes.length
      };
    }).sort((a, b) => parseFloat(b.promedioClase) - parseFloat(a.promedioClase));
  };

  const formatearFechaRelativa = (fecha) => {
    if (!fecha) return 'Nunca';
    
    const ahora = new Date();
    const diff = ahora - fecha;
    const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
    const horas = Math.floor(diff / (1000 * 60 * 60));
    const minutos = Math.floor(diff / (1000 * 60));
    
    if (dias > 30) return `Hace ${Math.floor(dias / 30)} mes(es)`;
    if (dias > 0) return `Hace ${dias} día(s)`;
    if (horas > 0) return `Hace ${horas} hora(s)`;
    if (minutos > 0) return `Hace ${minutos} min`;
    return 'Hace un momento';
  };

  // Preparar datos para gráfica de progreso
  const prepararDatosGrafica = () => {
    return filteredClases
      .filter(clase => clase.fecha_inicio && clase.fecha_fin)
      .map(clase => {
        const progreso = calcularProgreso(clase.fecha_inicio, clase.fecha_fin);
        return {
          nombre: clase.titulo || clase.nombre || 'Sin nombre',
          transcurrido: progreso.porcentaje,
          restante: 100 - progreso.porcentaje,
          diasRestantes: progreso.diasRestantes,
          activa: clase.activa
        };
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre)); // Ordenar alfabéticamente para la gráfica
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-semibold text-gray-900">{data.nombre}</p>
          <p className="text-sm text-blue-600">Transcurrido: {data.transcurrido.toFixed(1)}%</p>
          <p className="text-sm text-green-600">Restante: {data.restante.toFixed(1)}%</p>
          <p className="text-sm text-gray-600">Días restantes: {data.diasRestantes}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando información académica...</p>
        </div>
      </div>
    );
  }

  const datosGrafica = prepararDatosGrafica();
  const actividadProfesores = calcularActividadProfesores();
  const rendimientoClases = calcularRendimientoClases();

  // Calcular promedios por estudiante para gráfica
  const calcularPromediosPorEstudiante = () => {
    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
    
    // Si hay un estudiante seleccionado específico
    if (estudianteSeleccionado !== 'todos') {
      const estudianteData = estudiantes.find(e => e.id === estudianteSeleccionado);
      if (!estudianteData) return { promedios: [], promedioGeneral: 0, estudianteNombre: '' };
      
      // Obtener calificaciones del estudiante
      const calificacionesEstudiante = calificaciones.filter(cal => cal.estudiante_id === estudianteSeleccionado);
      
      // Agrupar por clase
      const porClase = {};
      calificacionesEstudiante.forEach(cal => {
        const clase = clases.find(c => c.id === cal.clase_id);
        const claseNombre = clase ? (clase.titulo || clase.nombre || 'Sin nombre') : 'Sin nombre';
        
        if (!porClase[claseNombre]) {
          porClase[claseNombre] = [];
        }
        porClase[claseNombre].push(cal.calificacion);
      });
      
      // Calcular promedio por clase
      const promedios = Object.entries(porClase).map(([nombre, notas]) => ({
        name: nombre,
        value: Number((notas.reduce((sum, n) => sum + n, 0) / notas.length).toFixed(1)),
        count: notas.length
      }));
      
      const promedioGeneral = promedios.length > 0
        ? Number((promedios.reduce((sum, p) => sum + p.value, 0) / promedios.length).toFixed(1))
        : 0;
      
      return { 
        promedios, 
        promedioGeneral, 
        estudianteNombre: estudianteData.nombre,
        colors: COLORS
      };
    }
    
    // Si es "todos", mostrar promedio general de todos los estudiantes
    const promediosPorEstudiante = estudiantes.map(estudiante => {
      const calificacionesEstudiante = calificaciones.filter(cal => cal.estudiante_id === estudiante.id);
      
      if (calificacionesEstudiante.length === 0) return null;
      
      const promedio = calificacionesEstudiante.reduce((sum, cal) => sum + cal.calificacion, 0) / calificacionesEstudiante.length;
      
      return {
        name: estudiante.nombre,
        value: Number(promedio.toFixed(1)),
        count: calificacionesEstudiante.length
      };
    }).filter(Boolean).sort((a, b) => b.value - a.value);
    
    const promedioGeneral = promediosPorEstudiante.length > 0
      ? Number((promediosPorEstudiante.reduce((sum, p) => sum + p.value, 0) / promediosPorEstudiante.length).toFixed(1))
      : 0;
    
    return {
      promedios: promediosPorEstudiante,
      promedioGeneral,
      estudianteNombre: 'Todos los Estudiantes',
      colors: COLORS
    };
  };

  const datosEstudiante = calcularPromediosPorEstudiante();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <i className="fas fa-chart-bar text-blue-600"></i>
          Panel de Control Académico
        </h1>
        <p className="text-gray-600 mt-2">
          Vista general del progreso y estado de todas las clases del instituto
        </p>
      </div>

      {/* Dashboard Ejecutivo - Métricas Generales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Total Estudiantes */}
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Estudiantes</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">{metricas.totalEstudiantes}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metricas.estudiantesActivos} activos
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="fas fa-user-graduate text-blue-600 text-xl"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Profesores */}
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Profesores</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{metricas.totalProfesores}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Total registrados
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-chalkboard-teacher text-green-600 text-xl"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clases Activas */}
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Clases Activas</p>
                <p className="text-3xl font-bold text-indigo-600 mt-2">{metricas.clasesActivas}</p>
                <p className="text-xs text-gray-500 mt-1">
                  En curso actual
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <i className="fas fa-book-open text-indigo-600 text-xl"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clases Inactivas */}
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Clases Inactivas</p>
                <p className="text-3xl font-bold text-gray-600 mt-2">{metricas.clasesInactivas}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Finalizadas
                </p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <i className="fas fa-book text-gray-600 text-xl"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Promedio General */}
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Promedio General</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">{metricas.promedioGeneral}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Del instituto
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="fas fa-chart-line text-purple-600 text-xl"></i>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Clases */}
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Clases</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">{clases.length}</p>
                <p className="text-xs text-gray-500 mt-1">
                  En el sistema
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <i className="fas fa-layer-group text-orange-600 text-xl"></i>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gestión de Capacitación de Profesores */}
      {profesoresPendientes.length > 0 && (
        <Card className="shadow-md border-l-4 border-orange-500">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fas fa-user-clock text-orange-600"></i>
                <span>Profesores Pendientes de Activación</span>
                <Badge variant="destructive" className="ml-2">
                  {profesoresPendientes.length}
                </Badge>
              </div>
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Lista de profesores nuevos que deben completar la capacitación antes de acceder al sistema
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {profesoresPendientes.map((profesor) => (
                <div 
                  key={profesor.id} 
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    profesor.capacitacion_completada 
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:shadow-md'
                      : 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200 hover:shadow-md'
                  } transition-shadow`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      profesor.capacitacion_completada ? 'bg-green-200' : 'bg-orange-200'
                    }`}>
                      <i className={`fas fa-user text-xl ${
                        profesor.capacitacion_completada ? 'text-green-700' : 'text-orange-700'
                      }`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {profesor.nombre} {profesor.apellido}
                      </h4>
                      <p className="text-sm text-gray-600">{profesor.email}</p>
                      
                      {profesor.capacitacion_completada ? (
                        <div className="mt-1">
                          <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                            <i className="fas fa-check-circle"></i>
                            Capacitación completada
                          </p>
                          {profesor.capacitacion_fecha && (
                            <p className="text-xs text-gray-500">
                              Fecha: {new Date(profesor.capacitacion_fecha).toLocaleDateString('es-UY')}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-orange-700 flex items-center gap-1 mt-1">
                          <i className="fas fa-hourglass-half"></i>
                          Pendiente de completar capacitación
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {profesor.capacitacion_completada ? (
                    <button
                      onClick={() => activarProfesor(profesor.id)}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-md"
                    >
                      <i className="fas fa-check"></i>
                      Activar Profesor
                    </button>
                  ) : (
                    <div className="text-center px-4">
                      <p className="text-sm text-gray-500 italic">
                        Esperando que complete<br/>la capacitación
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Métricas de Rendimiento Académico por Clase */}
      {rendimientoClases.length > 0 && rendimientoClases.some(c => c.totalCalificaciones > 0) && (
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <i className="fas fa-trophy text-yellow-600"></i>
              Rendimiento Académico por Clase
            </CardTitle>
            <p className="text-sm text-gray-600">
              Promedios y tasas de aprobación de las clases filtradas
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rendimientoClases
                .filter(clase => clase.totalCalificaciones > 0)
                .slice(0, 9)
                .map((clase) => (
                <div key={clase.id} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm line-clamp-2">
                    {clase.titulo || clase.nombre}
                  </h4>
                  
                  <div className="space-y-2">
                    {/* Promedio de la clase */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Promedio:</span>
                      <span className={`text-lg font-bold ${
                        parseFloat(clase.promedioClase) >= 80 ? 'text-green-600' :
                        parseFloat(clase.promedioClase) >= 70 ? 'text-blue-600' :
                        'text-orange-600'
                      }`}>
                        {clase.promedioClase}
                      </span>
                    </div>
                    
                    {/* Tasa de aprobación */}
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600">Aprobación:</span>
                        <span className="font-semibold text-gray-900">{clase.tasaAprobacion}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            parseFloat(clase.tasaAprobacion) >= 80 ? 'bg-green-500' :
                            parseFloat(clase.tasaAprobacion) >= 60 ? 'bg-blue-500' :
                            'bg-orange-500'
                          }`}
                          style={{ width: `${clase.tasaAprobacion}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Estudiantes calificados */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="text-xs text-gray-600">
                        <i className="fas fa-users mr-1"></i>
                        {clase.estudiantesCalificados} estudiantes
                      </span>
                      <span className="text-xs text-gray-600">
                        <i className="fas fa-file-alt mr-1"></i>
                        {clase.totalCalificaciones} notas
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gráfica de Promedios por Estudiante */}
      <Card className="shadow-lg border-0">
        <CardHeader className="pb-4 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardTitle className="flex items-center text-2xl">
            <i className="fas fa-chart-pie text-purple-600 mr-3"></i>
            Promedios de Estudiantes
          </CardTitle>
          
          {/* Filtro de Estudiante */}
          <div className="mt-4">
            <Label className="text-sm font-medium text-gray-700 mb-2 block">
              <i className="fas fa-user-graduate mr-2 text-purple-600"></i>
              Seleccionar Estudiante
            </Label>
            <Select 
              value={estudianteSeleccionado} 
              onValueChange={(value) => setEstudianteSeleccionado(value)}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Selecciona un estudiante" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">
                  <div className="flex items-center gap-2 font-semibold">
                    <i className="fas fa-users text-purple-600"></i>
                    Todos los Estudiantes
                  </div>
                </SelectItem>
                <div className="h-px bg-gray-200 my-1"></div>
                {estudiantes
                  .sort((a, b) => a.nombre.localeCompare(b.nombre))
                  .map((estudiante) => (
                    <SelectItem key={estudiante.id} value={estudiante.id}>
                      <div className="flex items-center gap-2">
                        <i className="fas fa-user text-blue-600"></i>
                        <span>{estudiante.nombre}</span>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        
        <CardContent>
          {datosEstudiante.promedios.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <i className="fas fa-chart-bar text-gray-400 text-4xl mb-3"></i>
              <p className="text-gray-600">No hay calificaciones disponibles para mostrar</p>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-100">
              <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
                {/* Gráfica */}
                <div className="relative" style={{ width: 280, height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={datosEstudiante.promedios}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {datosEstudiante.promedios.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={datosEstudiante.colors[index % datosEstudiante.colors.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                                <p className="font-semibold text-gray-800">{payload[0].name}</p>
                                <p className="text-purple-600 font-bold text-lg">{payload[0].value}</p>
                                <p className="text-xs text-gray-500">{payload[0].payload.count} calificaciones</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Promedio en el centro */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                    <div className="text-4xl font-bold text-purple-600">
                      {datosEstudiante.promedioGeneral}
                    </div>
                    <div className="text-xs text-gray-600 font-medium mt-1">
                      Promedio General
                    </div>
                  </div>
                </div>
                
                {/* Leyenda */}
                <div className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-2">
                  {datosEstudiante.promedios.map((entry, index) => (
                    <div key={index} className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-sm">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: datosEstudiante.colors[index % datosEstudiante.colors.length] }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{entry.name}</p>
                        <p className="text-xs text-gray-500">{entry.count} calificaciones</p>
                      </div>
                      <div className="text-lg font-bold" style={{ color: datosEstudiante.colors[index % datosEstudiante.colors.length] }}>
                        {entry.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Información adicional */}
              <div className="mt-6 pt-4 border-t border-purple-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    <i className="fas fa-user-graduate mr-2"></i>
                    {datosEstudiante.estudianteNombre}
                  </span>
                  <span className="text-gray-600">
                    <i className="fas fa-book mr-2"></i>
                    {datosEstudiante.promedios.length} {datosEstudiante.promedios.length === 1 ? 'clase' : 'clases'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filtros Globales - Afectan Gráficas y Detalle de Clases */}
      <Card className="shadow-md bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
        <CardContent className="p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
              <i className="fas fa-sliders-h"></i>
              Filtros Globales
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Los filtros afectan las gráficas de progreso y el detalle de clases mostrado abajo
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtro por Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-filter mr-2"></i>Estado de Clases
              </label>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="todas">Todas las clases</option>
                <option value="activas">Solo activas</option>
                <option value="inactivas">Solo inactivas</option>
              </select>
            </div>

            {/* Filtro por Semestre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-calendar-alt mr-2"></i>Semestre
              </label>
              <select
                value={semestreSeleccionado}
                onChange={(e) => setSemestreSeleccionado(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="todos">Todos los semestres</option>
                {semestres.map((semestre) => {
                  // Usar el campo 'name' que es el que se usa en Gestión de Semestres
                  const nombreSemestre = semestre.name || semestre.nombre || `Semestre ${semestre.semester_number || semestre.numero || ''}`;
                  
                  return (
                    <option key={semestre.id} value={semestre.id}>
                      {nombreSemestre}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Estadísticas rápidas */}
            <div className="flex items-center justify-around bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {filteredClases.filter(c => c.activa).length}
                </div>
                <div className="text-xs text-gray-600">Activas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {filteredClases.filter(c => !c.activa).length}
                </div>
                <div className="text-xs text-gray-600">Inactivas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">
                  {filteredClases.length}
                </div>
                <div className="text-xs text-gray-600">Total</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráfica de Progreso Temporal - Desktop */}
      {datosGrafica.length > 0 && (
        <>
          {/* Vista Desktop - Gráfica completa */}
          <Card className="shadow-md hidden md:block">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="fas fa-chart-line text-green-600"></i>
                Progreso Temporal de las Clases
              </CardTitle>
              <p className="text-sm text-gray-600">
                Visualización del tiempo transcurrido y restante de cada clase activa
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(400, datosGrafica.length * 50)}>
                <BarChart
                  data={datosGrafica}
                  layout="vertical"
                  margin={{ top: 40, right: 30, left: 150, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis 
                    type="category" 
                    dataKey="nombre" 
                    width={140}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="top" 
                    height={40}
                    wrapperStyle={{ paddingBottom: '15px' }}
                    iconType="square"
                  />
                  <Bar dataKey="transcurrido" stackId="a" name="Tiempo Transcurrido" fill="#3B82F6" />
                  <Bar dataKey="restante" stackId="a" name="Tiempo Restante" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
              
              {/* Leyenda manual adicional por si acaso */}
              <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span className="text-sm text-gray-700">Tiempo Transcurrido</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-sm text-gray-700">Tiempo Restante</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vista Mobile - Tarjetas compactas */}
          <Card className="shadow-md md:hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <i className="fas fa-chart-line text-green-600"></i>
                Progreso de Clases
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {datosGrafica.map((clase, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    {/* Nombre de la clase */}
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm text-gray-900 flex-1">
                        {clase.nombre}
                      </h4>
                      <span className={`text-lg font-bold ml-2 ${clase.activa ? 'text-blue-600' : 'text-gray-400'}`}>
                        {clase.transcurrido.toFixed(0)}%
                      </span>
                    </div>
                    
                    {/* Barra de progreso */}
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${clase.transcurrido}%` }}
                      ></div>
                    </div>
                    
                    {/* Días restantes */}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-600">
                        <i className="fas fa-clock mr-1"></i>
                        {clase.diasRestantes} días restantes
                      </span>
                      {!clase.activa && (
                        <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded">
                          Inactiva
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Leyenda para móvil */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span className="text-gray-600">Transcurrido</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-gray-200 rounded"></div>
                    <span className="text-gray-600">Restante</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Lista de Clases */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            Detalle de Clases 
            <span className="text-blue-600">({filteredClases.length})</span>
          </h2>
          {(filtroEstado !== 'todas' || semestreSeleccionado !== 'todos') && (
            <div className="flex items-center gap-2 text-sm">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                <i className="fas fa-filter text-xs"></i>
                Filtrado
              </span>
              <button
                onClick={() => {
                  setFiltroEstado('todas');
                  setSemestreSeleccionado('todos');
                }}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full text-xs flex items-center gap-1 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
        
        {filteredClases.length === 0 ? (
          <Card className="shadow-md">
            <CardContent className="p-12 text-center">
              <i className="fas fa-inbox text-gray-300 text-6xl mb-4"></i>
              <p className="text-gray-500 text-lg">No hay clases que coincidan con los filtros seleccionados</p>
            </CardContent>
          </Card>
        ) : (
          filteredClases.map((clase) => {
            const progreso = calcularProgreso(clase.fecha_inicio, clase.fecha_fin);
            
            return (
              <Card key={clase.id} className="shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna 1: Información Principal */}
                    <div className="lg:col-span-2 space-y-4">
                      {/* Título y Estado */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {clase.titulo || clase.nombre || 'Sin nombre'}
                          </h3>
                          <div className="flex flex-wrap gap-2 items-center">
                            <Badge className={clase.activa ? 'bg-green-500' : 'bg-gray-500'}>
                              <i className={`fas ${clase.activa ? 'fa-check-circle' : 'fa-times-circle'} mr-1`}></i>
                              {clase.activa ? 'Activa' : 'Inactiva'}
                            </Badge>
                            
                            {clase.semestres && clase.semestres.length > 0 && (
                              <Badge className="bg-blue-500">
                                <i className="fas fa-graduation-cap mr-1"></i>
                                {clase.semestres.map(sId => getSemestreNombre(sId)).join(', ')}
                              </Badge>
                            )}
                            
                            <Badge className="bg-purple-500">
                              <i className="fas fa-calendar-day mr-1"></i>
                              {getDiasFormateados(clase.dias_semana)}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Grid de Información */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Profesor */}
                        <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-user-tie text-white"></i>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">Profesor</div>
                            <div className="font-semibold text-gray-900">
                              {clase.profesor_nombre || 'No asignado'}
                            </div>
                          </div>
                        </div>

                        {/* Estudiantes */}
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                          <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-users text-white"></i>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">Estudiantes Inscritos</div>
                            <div className="font-semibold text-gray-900">
                              {clase.estudiantes_inscritos?.length || 0} estudiantes
                            </div>
                          </div>
                        </div>

                        {/* Fecha Inicio */}
                        <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                          <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-play-circle text-white"></i>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">Fecha de Inicio</div>
                            <div className="font-semibold text-gray-900">
                              {formatearFecha(clase.fecha_inicio)}
                            </div>
                          </div>
                        </div>

                        {/* Fecha Fin */}
                        <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                          <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-flag-checkered text-white"></i>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600">Fecha de Finalización</div>
                            <div className="font-semibold text-gray-900">
                              {formatearFecha(clase.fecha_fin)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Columna 2: Progreso */}
                    <div className="flex flex-col justify-center bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-6">
                      <div className="text-center mb-4">
                        <div className="text-4xl font-bold text-blue-600 mb-1">
                          {progreso.porcentaje}%
                        </div>
                        <div className="text-sm text-gray-600">Progreso Temporal</div>
                      </div>

                      {/* Barra de progreso */}
                      <div className="mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-500"
                            style={{ width: `${progreso.porcentaje}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Días restantes */}
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-2xl font-bold text-blue-600">
                            {progreso.diasTranscurridos}
                          </div>
                          <div className="text-xs text-gray-600">Días transcurridos</div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-2xl font-bold text-green-600">
                            {progreso.diasRestantes}
                          </div>
                          <div className="text-xs text-gray-600">Días restantes</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Control de Actividad de Profesores */}
      {actividadProfesores.length > 0 && (
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <i className="fas fa-user-tie text-purple-600"></i>
              Control de Actividad de Profesores
            </CardTitle>
            <p className="text-sm text-gray-600">
              Monitoreo de actividad y movimientos de los profesores en el sistema
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Profesor</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Clases</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Estudiantes</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Última Actividad</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {actividadProfesores.map((profesor, index) => (
                    <tr key={profesor.id} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <i className="fas fa-user text-purple-600"></i>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {profesor.nombre} {profesor.apellido}
                            </div>
                            <div className="text-xs text-gray-500">{profesor.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="text-sm">
                          <span className="font-semibold text-blue-600">{profesor.clasesActivas}</span>
                          <span className="text-gray-400"> / </span>
                          <span className="text-gray-600">{profesor.clasesAsignadas}</span>
                        </div>
                        <div className="text-xs text-gray-500">activas / total</div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="text-lg font-semibold text-green-600">
                          {profesor.totalEstudiantes}
                        </div>
                        <div className="text-xs text-gray-500">estudiantes</div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="text-sm text-gray-900">
                          {formatearFechaRelativa(profesor.ultimaActividadReal)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {profesor.ultimaActividadReal ? `${profesor.ultimaActividadReal.toLocaleDateString('es-UY')} ${profesor.ultimaActividadReal.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}` : '-'}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        {profesor.diasInactivo !== null ? (
                          profesor.diasInactivo === 0 && profesor.horasInactivo < 24 ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <i className="fas fa-circle mr-1 text-green-500 animate-pulse"></i>
                              En línea hoy
                            </span>
                          ) : profesor.diasInactivo > 7 ? (
                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              <i className="fas fa-exclamation-triangle mr-1"></i>
                              Inactivo {profesor.diasInactivo}d
                            </span>
                          ) : profesor.diasInactivo > 3 ? (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                              <i className="fas fa-clock mr-1"></i>
                              {profesor.diasInactivo} días
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <i className="fas fa-check-circle mr-1"></i>
                              Activo
                            </span>
                          )
                        ) : (
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                            Sin actividad
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PanelControlAcademico;
